import { useState, useCallback, useRef, useEffect } from 'react';
import ChatMessageList from './ChatMessageList';
import ChatInput, { ChatInputHandle } from './ChatInput';
import ChatEmptyState from './ChatEmptyState';
import CoverageInfo from './CoverageInfo';
import ModoSelector from './ModoSelector';
import { useChatSession } from '../../contexts/ChatSessionContext';
import {
  ChatMessageResponse, Citation, StreamCallbacks,
  sendMessage, getSessionMessages
} from '../../services/chatbot.service';
import { Modo, buildModoReceipt, loadStoredModo, storeModo } from '../../utils/modo';
import { ActiveTool, Stage, statusLabel } from '../../utils/statusLine';
import { reconcileLastTurn } from '../../utils/reconcileMessageIds';

interface ChatContainerProps {
  onDocumentClick: (citation: Citation, messageCitations: Citation[]) => void;
  initialQuery?: string;
  onTurnSettled?: (t: { sessionId: string; message: ChatMessageResponse }) => void;
  onDraftChange?: (hasText: boolean) => void;
  onStreamingChange?: (streaming: boolean) => void;
}

export default function ChatContainer({ onDocumentClick, initialQuery, onTurnSettled, onDraftChange, onStreamingChange }: ChatContainerProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingCitations, setStreamingCitations] = useState<Citation[]>([]);
  const [activeTools, setActiveTools] = useState<ActiveTool[]>([]);
  const [stage, setStage] = useState<Stage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modo, setModo] = useState<Modo>(loadStoredModo);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<ChatInputHandle>(null);
  const initialQuerySent = useRef(false);
  // Conversación vigente: un turno que termina de cargarse en otra ya no se avisa.
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = sessionId;

  const onStreamingChangeRef = useRef(onStreamingChange);
  onStreamingChangeRef.current = onStreamingChange;
  useEffect(() => { onStreamingChangeRef.current?.(isStreaming); }, [isStreaming]);

  const handleModoChange = useCallback((value: Modo) => {
    setModo(value);
    storeModo(value);
  }, []);

  const chatSession = useChatSession();

  // Register callbacks so Sidebar can control this ChatContainer
  const handleSelectSession = useCallback(async (id: string) => {
    try {
      const { messages: msgs } = await getSessionMessages(id);
      setSessionId(id);
      setMessages(msgs);
      inputRef.current?.clear();
      setStreamingContent('');
      setActiveTools([]);
      setError(null);
    } catch {
      setError('Error cargando sesion');
    }
  }, []);

  const handleNewSession = useCallback(() => {
    inputRef.current?.clear();
    setSessionId(null);
    setMessages([]);
    setStreamingContent('');
    setActiveTools([]);
    setError(null);
  }, []);

  // Dependen de las funciones (estables) y no del objeto del contexto, que cambia con cada lista.
  const registerCallbacks = chatSession?.registerCallbacks;
  const refreshSessions = chatSession?.refreshSessions;
  const setCurrentSessionId = chatSession?.setCurrentSessionId;
  // Sync sessionId to context. Va ANTES del registro de abajo: así el null inicial de `sessionId` se ignora
  // mientras el contexto aún tiene pendiente una conversación elegida desde otra pestaña (la registra y la abre).
  useEffect(() => {
    setCurrentSessionId?.(sessionId);
  }, [sessionId, setCurrentSessionId]);

  // Register with context on mount
  useEffect(() => {
    registerCallbacks?.({
      selectSession: handleSelectSession,
      newSession: handleNewSession,
    });
    refreshSessions?.();
    // Al desmontarse (otra pestaña) la barra lateral deja de poder mandar al chat; la selección queda pendiente.
    // Tampoco queda una conversación «abierta»: fuera del chat ninguna se marca activa en la barra, y al volver
    // el chat parte vacío sin mostrar un instante el título de la anterior. (Con una selección pendiente, el
    // contexto ignora este null.)
    return () => {
      registerCallbacks?.(null);
      setCurrentSessionId?.(null);
    };
  }, [registerCallbacks, refreshSessions, setCurrentSessionId, handleSelectSession, handleNewSession]);

  // Handle initial query from deep link (?q=...)
  useEffect(() => {
    if (initialQuery && !initialQuerySent.current) {
      initialQuerySent.current = true;
      handleSend(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  const handleSend = useCallback(async (message: string) => {
    if (isStreaming) return;

    setError(null);
    setIsStreaming(true);
    setStreamingContent('');
    setStreamingCitations([]);
    setActiveTools([]);
    setStage(null);

    // Add user message to UI immediately
    const userMsg: ChatMessageResponse = {
      id: 'temp-user-' + Date.now(),
      role: 'user',
      content: message,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);

    // Create abort controller
    const controller = new AbortController();
    abortRef.current = controller;

    let finalContent = '';
    let finalCitations: Citation[] = [];
    let newSessionId = sessionId;

    // «modo · N fuentes · N s» al terminar: el modo con que se envió este turno y el tiempo desde el envío.
    const turnModo = modo;
    const requestStartTime = Date.now();
    const turnStartIso = new Date(requestStartTime).toISOString();

    const callbacks: StreamCallbacks = {
      onSession: (id) => {
        newSessionId = id;
        setSessionId(id);
        chatSession?.refreshSessions();
      },
      onStage: (etapa, detalle) => setStage({ etapa, detalle }),
      onToolStart: (tool) => {
        setActiveTools(prev => [...prev, { tool, status: 'loading' }]);
      },
      onToolResult: (tool) => {
        setActiveTools(prev => prev.map(tc => (tc.tool === tool && tc.status === 'loading' ? { ...tc, status: 'done' as const } : tc)));
      },
      onContentDelta: (delta) => {
        finalContent += delta;
        setStreamingContent(finalContent);
      },
      onContentDone: (content, citations) => {
        finalContent = content;
        finalCitations = citations;
        setStreamingCitations(citations);
      },
      onError: (msg) => {
        let displayMsg = msg;
        if (msg.includes('Demasiadas peticiones') || msg.includes('Demasiados mensajes') || msg.includes('429')) {
          displayMsg = 'Has enviado muchas consultas en poco tiempo. Espera un momento antes de intentar de nuevo.';
        } else if (msg.includes('timeout') || msg.includes('tomo demasiado tiempo')) {
          displayMsg = 'La consulta tomó demasiado tiempo. Intenta con una pregunta más específica.';
        }
        setError(displayMsg);
      },
      onDone: () => {
        // Move streaming content into a proper message
        if (finalContent) {
          const elapsedSec = (Date.now() - requestStartTime) / 1000;
          const assistantMsg: ChatMessageResponse = {
            id: 'msg-' + Date.now(),
            role: 'assistant',
            content: finalContent,
            citations: finalCitations,
            created_at: new Date().toISOString(),
            reasoningReceipt: buildModoReceipt(turnModo, finalCitations, elapsedSec)
          };
          setMessages(prev => [...prev, assistantMsg]);

          if (newSessionId) {
            const sid = newSessionId;
            getSessionMessages(sid)
              .then(({ messages: serverMsgs }) => {
                setMessages(prev => reconcileLastTurn(prev, serverMsgs, turnStartIso));
                // El turno reconciliado se deriva de lo que devolvió el servidor, fuera del updater.
                const settledAfter = requestStartTime - 5000;
                let settled: ChatMessageResponse | undefined;
                for (let i = serverMsgs.length - 1; i >= 0; i--) {
                  const m = serverMsgs[i];
                  if (m.role === 'assistant' && new Date(m.created_at).getTime() >= settledAfter) { settled = m; break; }
                }
                if (settled && sessionIdRef.current === sid) onTurnSettled?.({ sessionId: sid, message: settled });
              })
              .catch(() => { /* sin id real: el Asistente no se dispara para este turno */ });
          }
        }
        setStreamingContent('');
        setStreamingCitations([]);
        setActiveTools([]);
        setStage(null);
        setIsStreaming(false);
        chatSession?.refreshSessions();
      }
    };

    try {
      await sendMessage(newSessionId, message, callbacks, controller.signal, modo);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Error de conexion');
      }
    } finally {
      setIsStreaming(false);
      setActiveTools([]);
      setStage(null);
      abortRef.current = null;
    }
  }, [isStreaming, sessionId, chatSession, modo, onTurnSettled]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
    if (streamingContent) {
      setMessages(prev => [...prev, {
        id: 'msg-partial-' + Date.now(),
        role: 'assistant',
        content: streamingContent + '\n\n*[Respuesta interrumpida]*',
        citations: streamingCitations,
        created_at: new Date().toISOString()
      }]);
      setStreamingContent('');
      setStreamingCitations([]);
    }
    setActiveTools([]);
    setStage(null);
  }, [streamingContent, streamingCitations]);

  const hasMessages = messages.length > 0 || isStreaming;

  // Selector de modo junto al botón de enviar (M-1); el aviso de cobertura queda a la izquierda de la misma fila.
  const composerControls = <ModoSelector value={modo} onChange={handleModoChange} />;
  const composerLeading = <CoverageInfo />;
  const currentStatus = statusLabel({ stage, activeTools, hasContent: !!streamingContent });

  return (
    <div className="flex flex-col h-[calc(100vh-85px)] bg-white">
      {/* Vacío o con conversación, la caja es la misma y queda al pie: al enviar no se mueve (DESIGN.md §12). */}
      {!hasMessages ? (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          <div className="mt-auto">
            <ChatEmptyState onSuggestionClick={q => inputRef.current?.fill(q)} />
          </div>
        </div>
      ) : (
        <ChatMessageList
          messages={messages}
          statusLabel={currentStatus}
          streamingContent={streamingContent}
          streamingCitations={streamingCitations}
          isStreaming={isStreaming}
          onDocumentClick={onDocumentClick}
          onSuggestionClick={handleSend}
          error={error}
        />
      )}

      <ChatInput
        ref={inputRef}
        onSend={handleSend}
        onStop={handleStop}
        isStreaming={isStreaming}
        onDraftChange={onDraftChange}
        controls={composerControls}
        leading={composerLeading}
      />
    </div>
  );
}
